import '@fontsource/geist/400.css';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/source-serif-4/400.css';
import '@fontsource/source-serif-4/400-italic.css';

import './index.css';
// Folha do DS genui2 antes das folhas locais: ela `@import`a a do ui-kit e acrescenta o
// `@source` que varre os cards do genui-kit — sem ela nenhuma utilitária escrita num card
// (hotel, checkout, voucher…) é compilada, porque o Tailwind daqui só varre `./src`. A
// folha é inerte fora de `.genui2` (preflight escopado; utilitárias escopadas pelo
// `postcss.config.js`, que também as faz vencer as homônimas daqui) e o que ela
// emite em `:root` fica em `@layer theme` — o `tokens.css` local, sem layer, continua
// dono dos nomes que compartilha com ela.
import '@voll-intelligence/genui-kit/styles.css';
import '@web/ui/core/themes/globals.css';

// Runs the legacy-localStorage purge at eval time, before the app module graph.
// MUST remain the first @web/* import in this file, above every @web/* barrel
// import below, so the purge runs before the services barrel constructs Cognito.
import '@web/data/services/auth/purge_legacy_auth';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import '@web/i18n';

import { GlobalErrorHandler, LocaleRoot, SnackbarProvider } from '@web/ui/core';
import { env, installRequestIdInterceptor, queryClient } from '@web/core';
// Not through the barrel: the router imports every page, and the pages import
// `@web/core` (env), so the barrel exporting it closed an import cycle.
import { router } from '@web/core/router';
import { authSync } from '@web/data/services';
import { useAuthStore } from '@web/data/repositories';

async function boot(): Promise<void> {
  installRequestIdInterceptor([
    env.VITE_KANBAN_API_URL,
    env.VITE_ACCOUNTS_API_URL,
    env.VITE_CONVERSATION_API_URL,
    env.VITE_MICE_API_URL,
  ]);
  authSync.onRemote(() => {
    useAuthStore.getState().loadUser(null);
    globalThis.location.assign('/login');
  });
  await authSync.bootstrap();

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Root element #root not found in index.html');

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <GlobalErrorHandler />
          <LocaleRoot>
            <RouterProvider router={router} />
          </LocaleRoot>
          {env.DEV ? (
            <>
              <ReactQueryDevtools buttonPosition="bottom-right" />
              <TanStackRouterDevtools router={router} position="bottom-left" />
            </>
          ) : null}
        </SnackbarProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

await boot();
